import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { ChannelBadge } from "@/components/ChannelBadge";
import {
  UsersThree, PaperPlaneTilt, ChatCentered, Broadcast,
  TrendUp, InstagramLogo, WhatsappLogo,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";

const toneClass = (tone) => {
  if (tone === "green") return "text-[#25D366]";
  if (tone === "pink") return "text-[#E1306C]";
  return "text-white/60";
};

const Stat = ({ label, value, unit, icon: Icon, tone = "white", testid }) => (
  <div data-testid={testid} className="border border-white/10 bg-[#121212] p-6 hover:bg-[#161616] transition-colors">
    <div className="flex items-start justify-between">
      <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-white/40">{label}</div>
      <Icon size={18} weight="regular" className={toneClass(tone)} />
    </div>
    <div className="mt-4 flex items-baseline gap-2">
      <div className="font-heading text-4xl tracking-tight">{value}</div>
      {unit && <div className="text-sm text-white/40 font-mono">{unit}</div>}
    </div>
  </div>
);

const AXIS_TICK = { fill: "#71717A", fontSize: 11, fontFamily: "JetBrains Mono" };
const TOOLTIP_STYLE = {
  backgroundColor: "#0A0A0A",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 0,
};
const TOOLTIP_LABEL_STYLE = { color: "#fff", fontFamily: "JetBrains Mono", fontSize: 11 };

export default function DashboardPage() {
  const [m, setM] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard/metrics").then(({ data }) => {
      if (!cancelled) setM(data);
    });
    return () => { cancelled = true; };
  }, []);

  const timeline = useMemo(() => m?.timeline || [], [m]);

  return (
    <Layout>
      <div className="border-b border-white/10 px-10 py-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// overview</div>
          <h1 className="font-heading text-5xl tracking-tight mt-2">Dashboard</h1>
        </div>
        <div className="text-sm text-white/40 font-mono">last 7 days</div>
      </div>

      <div className="p-10 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat testid="stat-contacts" label="Contacts" value={m?.total_contacts ?? 0} icon={UsersThree} />
          <Stat testid="stat-sent" label="Sent (7d)" value={m?.messages_sent_7d ?? 0} icon={PaperPlaneTilt} />
          <Stat testid="stat-received" label="Received (7d)" value={m?.messages_received_7d ?? 0} icon={ChatCentered} />
          <Stat testid="stat-reply-rate" label="Reply rate" value={m?.reply_rate ?? 0} unit="%" icon={TrendUp} />
        </div>

        {/* Channel breakdown + chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="border border-white/10 bg-[#121212] p-6">
            <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-white/40 mb-4">Channels</div>
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-white/5 pb-3">
                <div className="flex items-center gap-2">
                  <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" />
                  <span className="text-sm">WhatsApp</span>
                </div>
                <div className="font-mono text-lg">{m?.whatsapp_contacts ?? 0}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <InstagramLogo size={20} weight="fill" className="text-[#E1306C]" />
                  <span className="text-sm">Instagram</span>
                </div>
                <div className="font-mono text-lg">{m?.instagram_contacts ?? 0}</div>
              </div>
            </div>

            <div className="mt-8 pt-4 border-t border-white/10 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Broadcast size={16} className="text-white/60" />
                <span className="text-white/60">Active cadences</span>
                <span className="ml-auto font-mono">{m?.active_cadences ?? 0}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <UsersThree size={16} className="text-white/60" />
                <span className="text-white/60">Running enrollments</span>
                <span className="ml-auto font-mono">{m?.active_enrollments ?? 0}</span>
              </div>
            </div>
          </div>

          <div className="border border-white/10 bg-[#121212] p-6 lg:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-white/40 mb-4">Volume (7d)</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                  />
                  <Bar dataKey="sent" fill="#25D366" radius={0} />
                  <Bar dataKey="received" fill="#E1306C" radius={0} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="border border-white/10 bg-[#121212] p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-white/40 mb-4">Getting started</div>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["01", "Connect channels", "Set up Instagram OAuth + WhatsApp Cloud from Integrations."],
              ["02", "Import contacts", "Add leads or import them from a spreadsheet."],
              ["03", "Launch a cadence", "Create rules per channel and enroll contacts."],
            ].map(([n, t, d]) => (
              <li key={n} className="border-l-2 border-white/20 pl-4">
                <div className="font-mono text-white/40 text-xs">{n}</div>
                <div className="font-medium mt-1">{t}</div>
                <div className="text-sm text-white/50 mt-1">{d}</div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </Layout>
  );
}
